import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { AuthError } from '@/components/auth/auth-error';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { EmptyState } from '@/components/empty-state';
import { messageForError } from '@/lib/api-client';
import { authApi } from '@/lib/api/auth';

const searchSchema = z.object({ token: z.string().optional() });

const schema = z
  .object({
    password: z
      .string()
      .min(10, 'Use at least 10 characters')
      .max(200)
      .refine((value) => /[a-z]/i.test(value), 'Include at least one letter')
      .refine((value) => /[0-9]|[^a-z0-9]/i.test(value), 'Include a number or symbol'),
    confirm: z.string().min(1, 'Confirm your new password'),
  })
  .refine((values) => values.password === values.confirm, {
    path: ['confirm'],
    message: 'Both passwords must match',
  });

type Values = z.infer<typeof schema>;

function ResetPasswordPage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', confirm: '' },
  });

  async function onSubmit(values: Values) {
    if (!token) return;
    setFormError(null);
    try {
      await authApi.resetPassword({ token, password: values.password });
      toast.success('Password updated. Sign in with your new password.');
      void navigate({ to: '/login' });
    } catch (error) {
      setFormError(messageForError(error, 'That reset link is no longer valid.'));
    }
  }

  const pending = form.formState.isSubmitting;

  if (!token) {
    return (
      <EmptyState
        kicker="Invalid link"
        title="This reset link is incomplete"
        description="Password reset links expire after an hour and can only be used once. Request a fresh one and try again."
        action={
          <Button asChild variant="secondary">
            <Link to="/forgot-password">Request a new link</Link>
          </Button>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1.5">
        <span className="section-label">Account recovery</span>
        <h1 className="text-title font-semibold tracking-tight text-ink">Choose a new password</h1>
      </header>

      <Form {...form}>
        <form noValidate onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <AuthError message={formError} />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>New password</FormLabel>
                <FormControl>
                  <Input type="password" autoComplete="new-password" disabled={pending} {...field} />
                </FormControl>
                <FormDescription>At least 10 characters, with a number or symbol.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirm"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirm password</FormLabel>
                <FormControl>
                  <Input type="password" autoComplete="new-password" disabled={pending} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" size="lg" disabled={pending} className="w-full">
            {pending ? 'Updating…' : 'Update password'}
          </Button>
        </form>
      </Form>
    </div>
  );
}

export const Route = createFileRoute('/(auth)/reset-password')({
  validateSearch: searchSchema,
  component: ResetPasswordPage,
});
